import { useAppNavigate } from "@base/model/hooks/useAppNavigate";
import { useMediaQuery } from "@base/model/hooks/useMediaQuery";
import { Tabs } from "@buildblockinc/buildblock-design-system";
import type { DailyLogSearchRouteParams } from "../model/dailyLogSearchState";

export type ProjectTab = "overview" | "documents" | "history";

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

const PROJECT_TABS: readonly ProjectTab[] = ["overview", "documents", "history"];

function isProjectTab(value: string): value is ProjectTab {
  return (PROJECT_TABS as readonly string[]).includes(value);
}

interface ProjectTabNavProps {
  projectId: string;
  activeTab: ProjectTab;
  /** 같은 프로젝트 안에서 탭을 오갈 때 유지할 Documents 검색 조건 */
  documentsSearch: DailyLogSearchRouteParams;
}

/**
 * 프로젝트 탭. 디자인 시스템 Tabs를 URL로 제어한다.
 * Overview·Documents는 `/projects/{id}?tab=` URL, Work History는 `/projects/{id}/history`로 이동한다.
 * 탭 이동은 history entry를 쌓지 않도록 replace한다. Documents 검색 조건은 Work History로 갈 때 history state로만 넘기며
 * Work History 집계에는 전달하지 않는다.
 */
export function ProjectTabNav({ projectId, activeTab, documentsSearch }: ProjectTabNavProps) {
  const navigate = useAppNavigate();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  const handleValueChange = (value: string) => {
    if (!isProjectTab(value) || value === activeTab) return;

    if (value === "history") {
      navigate({
        to: "/projects/$projectId/history",
        params: { projectId },
        state: { projectDocumentsSearch: documentsSearch },
        replace: true,
        direction: "none",
      });
      return;
    }

    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: { ...documentsSearch, tab: value },
      replace: true,
      direction: "none",
    });
  };

  return (
    <nav aria-label="Project sections" className="md:mt-4">
      <Tabs
        color="secondary"
        layout={isDesktop ? "hug" : "fill"}
        value={activeTab}
        onValueChange={({ value }) => handleValueChange(value)}
      >
        <Tabs.List>
          <Tabs.Trigger value="overview" className="px-4 pt-2 pb-4">
            Overview
          </Tabs.Trigger>
          <Tabs.Trigger value="documents" className="px-4 pt-2 pb-4">
            Documents
          </Tabs.Trigger>
          {/* 모바일은 Work History 탭 대신 Overview의 "Check work history"로 진입한다 */}
          <Tabs.Trigger value="history" className="hidden px-4 pt-2 pb-4 md:flex">
            Work History
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs>
    </nav>
  );
}
